import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDateShort, STATUS_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, FileText, Plus, Edit2,
  ShoppingBag, Wallet, AlarmClock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const StatTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-white border border-[#E5DCC7] p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-3">
      <div className="clf-label">{label}</div>
      <Icon className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
    </div>
    <div className={`text-2xl font-medium font-mono tracking-tight ${accent || "text-stone-900"}`}>
      {value}
    </div>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [{ data: cust }, { data: list }] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/orders?customer_id=${id}`),
      ]);
      setCustomer(cust);
      setOrders(list);
      setForm({
        name: cust.name, phone: cust.phone || "", email: cust.email || "",
        address: cust.address || "", notes: cust.notes || "",
      });
    } catch (e) {
      toast.error("Customer tidak ditemukan");
      nav("/customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  const save = async () => {
    if (!form.name) return toast.error("Nama wajib diisi");
    try {
      await api.put(`/customers/${id}`, form);
      toast.success("Customer diperbarui");
      setOpen(false);
      fetchAll();
    } catch (e) {
      toast.error("Gagal menyimpan");
    }
  };

  if (loading || !customer) {
    return <div className="p-4 lg:p-10 text-stone-500" data-testid="customer-detail-loading">Memuat...</div>;
  }

  // Aggregate stats from orders
  const totalOrders = orders.length;
  const totalSpent = orders.reduce((s, o) => s + (o.total || 0), 0);
  const totalPaid = orders.reduce((s, o) => s + ((o.total || 0) - (o.balance || 0)), 0);
  const totalBalance = orders.reduce((s, o) => s + (o.balance || 0), 0);
  const activeOrders = orders.filter((o) => !["diterima"].includes(o.status)).length;

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]" data-testid="customer-detail-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <Link to="/customers" className="text-xs text-stone-500 hover:text-[#A63D30] flex items-center gap-1 mb-3">
            <ArrowLeft className="h-3 w-3" /> Kembali ke daftar customer
          </Link>
          <div className="clf-label mb-2">Customer</div>
          <h1 className="text-3xl tracking-tight font-medium">{customer.name}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpen(true)} data-testid="edit-customer-btn"
            className="border border-[#D9CDB5] px-4 py-2.5 text-sm font-medium hover:bg-stone-100 flex items-center gap-2">
            <Edit2 className="h-4 w-4" strokeWidth={1.5} /> Edit
          </button>
          <Link to={`/orders/new?customer=${customer.id}`}
            data-testid="new-order-for-customer-btn"
            className="bg-[#16202B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#243345] flex items-center gap-2">
            <Plus className="h-4 w-4" strokeWidth={2} /> Order Baru
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Customer info */}
        <div className="bg-white border border-[#E5DCC7] p-6 space-y-4" data-testid="customer-info-card">
          <div className="clf-label">Kontak</div>
          <div className="space-y-2.5 text-sm">
            {customer.phone && (
              <div className="flex items-start gap-3">
                <Phone className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                <a href={`https://wa.me/${customer.phone.replace(/[^\d]/g, "")}`}
                   target="_blank" rel="noreferrer"
                   className="text-stone-800 hover:text-[#A63D30]">{customer.phone}</a>
              </div>
            )}
            {customer.email && (
              <div className="flex items-start gap-3">
                <Mail className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${customer.email}`} className="text-stone-800 hover:text-[#A63D30] break-all">
                  {customer.email}
                </a>
              </div>
            )}
            {customer.address && (
              <div className="flex items-start gap-3">
                <MapPin className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-stone-700">{customer.address}</span>
              </div>
            )}
            {customer.notes && (
              <div className="flex items-start gap-3 pt-2 border-t border-stone-100 mt-3">
                <FileText className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-stone-600 italic">{customer.notes}</span>
              </div>
            )}
            {!customer.phone && !customer.email && !customer.address && (
              <div className="text-xs text-stone-500 italic">Belum ada informasi kontak.</div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-3">
          <StatTile icon={ShoppingBag} label="Total Order" value={totalOrders}
            testid="cust-stat-total-orders" />
          <StatTile icon={AlarmClock} label="Sedang Berjalan" value={activeOrders}
            accent="text-amber-700" testid="cust-stat-active-orders" />
          <StatTile icon={Wallet} label="Total Belanja" value={formatRupiah(totalSpent)}
            testid="cust-stat-total-spent" />
          <StatTile icon={Wallet} label="Sisa Tagihan" value={formatRupiah(totalBalance)}
            accent={totalBalance > 0 ? "text-[#A63D30]" : "text-emerald-700"}
            testid="cust-stat-balance" />
        </div>
      </div>

      {/* Order history */}
      <section className="bg-white border border-[#E5DCC7]" data-testid="customer-orders-section">
        <div className="px-6 py-4 border-b border-[#E5DCC7] flex items-center justify-between">
          <div>
            <div className="clf-label mb-1">Riwayat Order</div>
            <h2 className="text-lg font-medium">
              {totalOrders} pesanan · Dibayar {formatRupiah(totalPaid)}
            </h2>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-stone-500 mb-4">
              Customer ini belum memiliki pesanan.
            </p>
            <Link to={`/orders/new?customer=${customer.id}`}
              className="text-[#A63D30] hover:text-[#8C3125] text-sm font-medium">
              + Buat order pertama
            </Link>
          </div>
        ) : (
          <div>
            <table className="w-full text-sm" data-testid="customer-orders-table">
            <thead className="bg-stone-50 border-b border-[#E5DCC7]">
              <tr>
                <th className="text-left clf-label px-6 py-3">No. Order</th>
                <th className="text-left clf-label px-6 py-3">Tgl Masuk</th>
                <th className="text-left clf-label px-6 py-3">Items</th>
                <th className="text-right clf-label px-6 py-3">Total</th>
                <th className="text-right clf-label px-6 py-3">Sisa</th>
                <th className="text-left clf-label px-6 py-3">Status</th>
                <th className="text-left clf-label px-6 py-3">Bayar</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}
                  data-testid={`cust-order-row-${o.id}`}
                  className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                  <td className="px-6 py-3 font-mono font-medium">
                    <Link to={`/orders/${o.id}`} className="hover:text-[#A63D30]">
                      {o.order_number}
                    </Link>
                  </td>
                  <td className="px-6 py-3 font-mono text-stone-700">
                    {formatDateShort(o.tanggal_masuk)}
                  </td>
                  <td className="px-6 py-3 text-stone-700">
                    {o.items?.length || 0} item
                    <span className="text-[11px] text-stone-500 ml-1">
                      ({o.items?.reduce((s, i) => s + (i.quantity || 0), 0)} pcs)
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right font-mono font-medium">
                    {formatRupiah(o.total)}
                  </td>
                  <td className={`px-6 py-3 text-right font-mono ${o.balance > 0 ? "text-[#A63D30]" : "text-stone-400"}`}>
                    {formatRupiah(o.balance)}
                  </td>
                  <td className="px-6 py-3">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-xs font-medium ${
                      o.payment_status === "lunas" ? "text-emerald-700" :
                      o.payment_status === "dp" ? "text-amber-700" : "text-stone-500"
                    }`}>
                      {o.payment_status.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#D9CDB5]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="clf-label block mb-1">Nama *</label>
              <input data-testid="cust-edit-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value.replace(/(^\w|\s\w)/g, m => m.toUpperCase()) })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="clf-label block mb-1">Telp / WA</label>
                <input data-testid="cust-edit-phone" inputMode="numeric" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="clf-label block mb-1">Email</label>
                <input data-testid="cust-edit-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
            </div>
            <div>
              <label className="clf-label block mb-1">Alamat</label>
              <textarea data-testid="cust-edit-address" value={form.address} rows={2}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Catatan</label>
              <textarea data-testid="cust-edit-notes" value={form.notes} rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-[#D9CDB5] hover:bg-stone-100">Batal</button>
            <button onClick={save} data-testid="cust-edit-save"
              className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345]">
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
